import { Button, CircularProgress, Stack, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import alqemiDataService from "../../../api/AlqemiDataService";
import alqemiService from "../../../api/AlqemiService";
import { BiggerIcon } from "../../../common/components/fundamentals/Icon";
import { useSnackbar } from "../../../common/context/SnackbarProvider";
import { useUser } from "../../../common/context/UserProvider";
import { FullStack } from "../../../common/styles/Box.styles";
import {
  borderStyle,
  defaultTransition,
  iconGap,
  perelynBlack,
  perelynPrimary,
  smallGap,
  tinyGap,
} from "../../../styles/consts";
import { hasSalesOrAdminAccess, isSupervisorFlowActive } from "../../../utils";
import PopUpBox from "../../components/PopUpBox";
import { useOffer } from "../../context/OfferProvider";
import { useStepNavigation } from "../../context/StepNavigationContext";
import { isConfirmed } from "../../utils/confirmationUtils";
import CandidateSelectionRow from "../CandidateSelectionRow";
import Explanation from "../Explanation";
import JobDetails from "../JobDetails";
import KeywordAnalysis from "../KeywordAnalysis";
import RejectionPopupContent from "../RejectionPopupContent";
import StepNavigation from "../StepNavigation";

export default function OverallReviewStep() {
  const { offer, setOffer } = useOffer();
  const { user } = useUser();
  const { showSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { offerId } = useParams();
  const { setCandidateStep } = useStepNavigation();

  const [displayedMatchEmail, setDisplayedMatchEmail] = useState("");
  const [closeRejectPopUp, setCloseRejectPopUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [showTranslated, setShowTranslated] = useState(false);
  const [isTranslatingDescription, setIsTranslatingDescription] =
    useState(false);

  const isArchived = !!offer.archived_at;

  const setCurrentCandidateInUrl = (email: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("currentCandidate", email);
    window.history.replaceState(null, "", `?${newSearchParams.toString()}`);
  };

  useEffect(() => {
    const currentCandidate = searchParams.get("currentCandidate");

    const candidateExists =
      currentCandidate &&
      offer.matches?.find((m) => m.candidate?.email === currentCandidate);

    if (candidateExists) {
      if (currentCandidate !== displayedMatchEmail) {
        setDisplayedMatchEmail(currentCandidate);
      }
    } else if (offer.matches && offer.matches.length > 0) {
      const matches = offer.matches;
      const highestScoreMatch = matches.find(
        (match) =>
          match.score === Math.max(...matches.map((m) => m.score || 0)),
      );
      const defaultEmail =
        highestScoreMatch?.candidate?.email ||
        matches[0]?.candidate?.email ||
        "";
      if (defaultEmail) {
        setDisplayedMatchEmail(defaultEmail);
        setCurrentCandidateInUrl(defaultEmail);
      }
    }
  }, [searchParams, offer.matches]);

  const displayedMatch = offer?.matches?.find(
    (match) => match.candidate?.email === displayedMatchEmail,
  );

  const isCandidate = displayedMatch?.candidate
    ? user.email === displayedMatch.candidate.email
    : false;
  const isSupervisor = displayedMatch?.candidate
    ? user.email === displayedMatch.candidate.supervisor_email
    : false;
  const isAlreadyConfirmedByConsultant = isConfirmed(
    displayedMatch?.consultant_confirmed,
  );

  // Read snapshot helper so future per-flow layout tweaks have a single hook;
  // both branches currently land in the same layout.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const supervisorFlowActive = isSupervisorFlowActive(displayedMatch);
  const showSupervisorView = !isCandidate;

  const handleCandidateClick = (email: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("currentCandidate", email);
    window.history.replaceState(null, "", `?${newSearchParams.toString()}`);
    setDisplayedMatchEmail(email);
  };

  const handleProceed = () => {
    if (displayedMatch) {
      if (isCandidate || isSupervisor || hasSalesOrAdminAccess(user)) {
        setCandidateStep(displayedMatchEmail, 2);
        navigate(
          `/alqemi/${offerId}/cv-adaptation?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`,
        );
      }
    }
  };

  const handleRejectOffer = async (
    selectedReasons: string[],
    freeText: string,
  ) => {
    setIsLoading(true);
    try {
      let rejectionReason = "";
      if (selectedReasons.length > 0) {
        rejectionReason = selectedReasons.join(", ");
      }
      if (freeText.trim() !== "") {
        if (rejectionReason !== "") {
          rejectionReason += "\n\nAdditional comments: " + freeText.trim();
        } else {
          rejectionReason = freeText.trim();
        }
      }

      const jobTitle = offer.title;
      await alqemiService.sendRejectionEmail({
        displayedMatch,
        rejectionReason,
        jobTitle,
      });

      const matchId = displayedMatch?.id || displayedMatch?.consultant_id;
      if (matchId) {
        await alqemiDataService.rejectMatch(matchId);
      }

      setIsRejected(true);
      setCloseRejectPopUp(true);
      showSnackbar("Offer successfully rejected");

      const updatedOffer = { ...offer };
      if (updatedOffer.matches) {
        const matchIndex = updatedOffer.matches.findIndex(
          (m) => m.candidate?.email === displayedMatchEmail,
        );
        if (matchIndex !== -1) {
          updatedOffer.matches[matchIndex].status = "rejected";
          setOffer(updatedOffer);
        }
      }
    } catch (error) {
      console.error("Error rejecting offer:", error);
      showSnackbar("Failed to reject offer. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsRejected(displayedMatch?.status === "rejected");
  }, [displayedMatch?.candidate?.email, displayedMatch?.status]);

  useEffect(() => {
    setShowTranslated(false);
  }, [offer.id]);

  const handleTranslate = async () => {
    if (offer.beschreibung_translated) {
      setShowTranslated(true);
      return;
    }
    setIsTranslatingDescription(true);
    try {
      const result = await alqemiDataService.translateDescription(offer.id);
      setOffer({ ...offer, beschreibung_translated: result.translated });
      setShowTranslated(true);
    } catch (err) {
      console.error("Description translation failed:", err);
      showSnackbar("Translation failed. Please try again.", true);
    } finally {
      setIsTranslatingDescription(false);
    }
  };

  return (
    <FullStack
      sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <Stack
        sx={{ padding: smallGap, gap: smallGap, overflow: "auto", flex: 1 }}
      >
        <CandidateSelectionRow
          matches={offer.matches}
          displayedMatchEmail={displayedMatchEmail}
          onCandidateClick={handleCandidateClick}
          showDetailedScores={hasSalesOrAdminAccess(user)}
        />

        {showSupervisorView ? (
          <Stack
            sx={{
              flexDirection: "row",
              gap: smallGap,
              flex: 1,
              ...(isArchived && { opacity: 0.5 }),
            }}
          >
            <Stack sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Stack sx={{ flex: 1 }}>
                <TextField
                  multiline
                  value={
                    showTranslated
                      ? (offer.beschreibung_translated ?? "")
                      : (offer.beschreibung ?? "")
                  }
                  InputProps={{ readOnly: true }}
                  sx={{
                    height: "100%",
                    "& .MuiInputBase-root": {
                      height: "100%",
                      alignItems: "flex-start",
                    },
                    "& .MuiInputBase-input": {
                      height: "100% !important",
                      overflow: "auto !important",
                      whiteSpace: "pre-wrap",
                    },
                  }}
                />
              </Stack>
              {offer.beschreibung && (
                <Stack sx={{ alignItems: "flex-start", paddingTop: tinyGap }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={isTranslatingDescription}
                    onClick={
                      showTranslated
                        ? () => setShowTranslated(false)
                        : handleTranslate
                    }
                    sx={{
                      width: "120px",
                      backgroundColor: isTranslatingDescription ? "#ffffff" : perelynPrimary,
                      border: borderStyle,
                      color: perelynBlack,
                      "&:hover": {
                        backgroundColor: perelynBlack,
                        color: perelynPrimary,
                        border: borderStyle,
                      },
                      "&:disabled": {
                        backgroundColor: "#ffffff",
                        border: borderStyle,
                        opacity: 1,
                      },
                    }}
                  >
                    {isTranslatingDescription
                      ? <CircularProgress size={20} sx={{ color: perelynBlack }} />
                      : showTranslated
                        ? "Show original"
                        : "Translate"}
                  </Button>
                </Stack>
              )}
            </Stack>

            <Stack sx={{ flex: 1, gap: smallGap }}>
              <JobDetails offer={offer} />
              {displayedMatch && (
                <Explanation displayedMatch={displayedMatch} />
              )}
              {displayedMatch?.keyword_analysis && (
                <KeywordAnalysis
                  keywordAnalysis={displayedMatch.keyword_analysis}
                  baselineKeywordAnalysis={displayedMatch.baseline_keyword_analysis}
                />
              )}
            </Stack>
          </Stack>
        ) : (
          <Stack
            sx={{
              flexDirection: "row",
              gap: smallGap,
              flex: 1,
              ...(isArchived && { opacity: 0.5 }),
            }}
          >
            <Stack sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Stack sx={{ flex: 1 }}>
                <TextField
                  multiline
                  value={
                    showTranslated
                      ? (offer.beschreibung_translated ?? "")
                      : (offer.beschreibung ?? "")
                  }
                  InputProps={{ readOnly: true }}
                  sx={{
                    height: "100%",
                    "& .MuiInputBase-root": {
                      height: "100%",
                      alignItems: "flex-start",
                    },
                    "& .MuiInputBase-input": {
                      height: "100% !important",
                      overflow: "auto !important",
                      whiteSpace: "pre-wrap",
                    },
                  }}
                />
              </Stack>
              {offer.beschreibung && (
                <Stack sx={{ alignItems: "flex-start", paddingTop: tinyGap }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={isTranslatingDescription}
                    onClick={
                      showTranslated
                        ? () => setShowTranslated(false)
                        : handleTranslate
                    }
                    sx={{
                      width: "120px",
                      backgroundColor: isTranslatingDescription ? "#ffffff" : perelynPrimary,
                      border: borderStyle,
                      color: perelynBlack,
                      "&:hover": {
                        backgroundColor: perelynBlack,
                        color: perelynPrimary,
                        border: borderStyle,
                      },
                      "&:disabled": {
                        backgroundColor: "#ffffff",
                        border: borderStyle,
                        opacity: 1,
                      },
                    }}
                  >
                    {isTranslatingDescription
                      ? <CircularProgress size={20} sx={{ color: perelynBlack }} />
                      : showTranslated
                        ? "Show original"
                        : "Translate"}
                  </Button>
                </Stack>
              )}
            </Stack>

            <Stack sx={{ flex: 1 }}>
              {
                displayedMatch && (
                  <Stack sx={{ gap: smallGap }}>
                    <JobDetails offer={offer} />
                    <Explanation displayedMatch={displayedMatch} />
                    {displayedMatch?.keyword_analysis && (
                      <KeywordAnalysis
                        keywordAnalysis={displayedMatch.keyword_analysis}
                        baselineKeywordAnalysis={displayedMatch.baseline_keyword_analysis}
                      />
                    )}

                    {isCandidate && (
                      <PopUpBox
                        heading="Reject Offer"
                        button={
                          <Button
                            variant="outlined"
                            disabled={
                              isAlreadyConfirmedByConsultant || isRejected
                            }
                            sx={{
                              ...(isRejected && {
                                borderColor: perelynPrimary,
                                color: perelynPrimary,
                              }),
                              gap: iconGap,
                              "&:hover": {
                                "& > *": {
                                  transition: defaultTransition,
                                },
                              },
                            }}
                          >
                            {isRejected ? "Offer Rejected" : "Reject Offer"}
                            <BiggerIcon name="XMark" />
                          </Button>
                        }
                        width="50%"
                        closePopUp={closeRejectPopUp}
                        setClosePopUp={setCloseRejectPopUp}
                      >
                        <RejectionPopupContent
                          onSubmit={handleRejectOffer}
                          onCancel={() => setCloseRejectPopUp(true)}
                          isLoading={isLoading}
                        />
                      </PopUpBox>
                    )}
                  </Stack>
                )
              }
            </Stack>
          </Stack>
        )}
      </Stack>

      <StepNavigation
        candidateEmail={displayedMatchEmail}
        onNext={handleProceed}
        nextDisabled={
          !displayedMatch ||
          (!isCandidate && !isSupervisor && !hasSalesOrAdminAccess(user))
        }
        previousDisabled={true}
      />
    </FullStack>
  );
}
